import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { INK, INK_2, INK_3, LINE, LINE_2 } from 'common/styles/listUI';
import { Search } from 'common/images/FaIcons';

/**
 * Shared toolbar atoms for the redesigned list pages (sc-35741). Extracted from
 * the Assets page so every migrated list page (Assets, Devices, ...) renders an
 * identical "Columns / Search / Add" toolbar. Compose these with the FaIcons
 * (ColumnIcon, Search, Plus) and the shared EditDropdown column picker.
 *
 * Colors come from the shared listUI tokens so every migrated page draws from a
 * single source of truth (see common/styles/listUI).
 */

export const ToolbarSearch = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  border: 1px solid ${LINE};
  border-radius: 7px;
  padding: 6px 11px;
  background: white;
  width: 220px;
  color: ${INK_3};
  transition: border-color 0.15s;
  box-sizing: border-box;

  &:focus-within { border-color: ${props => props.theme.primaryColor}; }
  svg { flex: 0 0 auto; width: 13px; height: 13px; }
`;

export const ToolbarSearchInput = styled.input`
  border: none;
  background: transparent;
  outline: none;
  font-family: inherit;
  font-size: 12px;
  color: ${INK};
  width: 100%;

  &::placeholder { color: ${INK_3}; }
`;

const ToolbarSearchClear = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  margin: 0;
  flex: 0 0 auto;
  cursor: pointer;
  font-family: inherit;
  font-size: 11px;
  line-height: 1;
  color: ${INK_3};

  &:hover { color: ${INK}; }
`;

/**
 * The toolbar search field: magnifier, input, and a one-click clear (✕) that
 * appears once there is something to clear. Every migrated list page composes
 * this rather than assembling ToolbarSearch/ToolbarSearchInput by hand, so the
 * clear affordance the legacy `InputField ... clearable` offered stays
 * available everywhere.
 *
 * Clearing re-uses the caller's own `onChange` with a synthetic empty-value
 * event, so it goes down exactly the same path as the user selecting the text
 * and deleting it — no page needs a separate clear handler.
 */
export const ToolbarSearchBox = ({
  name, placeholder, value, onChange, clearLabel
}) => (
  <ToolbarSearch>
    <Search />
    <ToolbarSearchInput
      name={name}
      type="text"
      placeholder={placeholder}
      onChange={onChange}
      value={value || ''}
    />
    {value ? (
      <ToolbarSearchClear
        type="button"
        title={clearLabel}
        aria-label={clearLabel}
        onClick={() => onChange({ target: { name, value: '' } })}
      >
        ✕
      </ToolbarSearchClear>
    ) : null}
  </ToolbarSearch>
);

ToolbarSearchBox.propTypes = {
  name: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  clearLabel: PropTypes.string
};

ToolbarSearchBox.defaultProps = {
  placeholder: '',
  value: '',
  clearLabel: 'Clear search'
};

export const GhostBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid transparent;
  background: transparent;
  color: ${INK_2};
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  border-radius: 7px;
  padding: 6px 10px;
  cursor: pointer;
  box-sizing: border-box;

  svg { width: 13px; height: 13px; }
  &:hover { background: ${LINE_2}; color: ${INK}; }
`;

const primaryCss = `
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  border-radius: 7px;
  padding: 6px 13px;
  cursor: pointer;
  white-space: nowrap;
  text-decoration: none;
  box-sizing: border-box;

  svg { width: 13px; height: 13px; }
`;

export const PrimaryLinkBtn = styled(Link)`
  ${primaryCss}
  background: ${props => props.theme.primaryColor};
  border: 1px solid ${props => props.theme.primaryColor};
  color: #fff;

  &:hover { filter: brightness(0.93); color: #fff; }
`;

export const PrimaryButton = styled.button`
  ${primaryCss}
  background: ${props => props.theme.primaryColor};
  border: 1px solid ${props => props.theme.primaryColor};
  color: #fff;

  &:hover { filter: brightness(0.93); color: #fff; }
`;
